import * as React from "react";
import { useRef, useState } from "react";

import { useHistory, useRouteMatch } from "react-router-dom";
import { RouterViewTemplate, useLoadingState } from "../RouterView";
import {
  CalculationBasis,
  CashbackConfigStatus,
  CashbackConfigV2,
  CashbackFrequencyV2,
  CashbackLevel,
  CashbackTypeV2,
  DayOfWeek,
  GameTag,
  ListGameTagResponse,
  RewardTemplate,
  RewardTemplateListResponse,
  RewardType,
} from "../../http/protocol";
import { Form, useForm } from "../form/Form";
import { getContainer } from "../../ioc/IOCSetup";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { Submit } from "../form/Submit";
import { Validators } from "../form/Validators";
import { SelectInput } from "../form/SelectInput";
import { t } from "../../i18n";

import { gameTagsFromString, gameTagsToString } from "./utils/GameTagUtils";
import { createLevelOverride, DEFAULT_LEVEL, LevelOverride, V2UserLevelsEditor } from "./components/V2UserLevelsEditor";
import {
  blockedFractionMessage,
  blockingFractionLimit,
  CashbackFractionThresholds,
  isLargeFraction,
  isNewlyBlockingFraction,
  largeFractionThreshold,
  useCashbackFractionThresholds,
} from "./cashbackFractionThresholds";
import { LargeFractionConfirmationDialog, LargeFractionEntry } from "../dialog/LargeFractionConfirmationDialog";
import { EXAMPLE_HOUSE_EDGE, EXAMPLE_RTP_PERCENT, EXAMPLE_WAGER, formatEuro } from "./cashbackFractionExample";
import "./styles/CashbackConfig.scss";

const HOURS: string[] = Array.from({ length: 24 }, (_, hour) => hour.toString().padStart(2, "0"));

function parseHour(time: string | undefined | null): string {
  if (!time) {
    return "";
  }
  const [hour] = time.split(":");
  return hour ?? "";
}

export function formatHour(hour: string): string {
  return `${hour.padStart(2, "0")}:00`;
}

function isValidOverrides(overrides: LevelOverride[]): boolean {
  const seenLevels = new Set<number>();
  for (const row of overrides) {
    const level = parseInt(row.level, 10);
    const fraction = parseFloat(row.fraction);
    if (Number.isNaN(level) || level < 0) {
      return false;
    }
    // A row whose fraction is inherited (never level 0, the floor) applies no fraction of its own,
    // so it needs no value here — this is what lets a level override only its game tags.
    const fractionApplies = level.toString() === DEFAULT_LEVEL || !row.fractionInherited;
    if (fractionApplies && (Number.isNaN(fraction) || fraction < 0)) {
      return false;
    }
    if (seenLevels.has(level)) {
      return false;
    }
    seenLevels.add(level);
  }
  return true;
}

function convertOverridesToAttributes(overrides: LevelOverride[]): Record<string, CashbackLevel> {
  const attributes: Record<string, CashbackLevel> = {};
  for (const row of overrides) {
    const level = parseInt(row.level, 10);
    if (Number.isNaN(level)) {
      continue;
    }
    // Level 0 is the inheritance floor and can never inherit, so its flags are forced off here too.
    const isDefault = level.toString() === DEFAULT_LEVEL;
    const gameTagsInherited = !isDefault && row.gameTagsInherited;
    const fractionInherited = !isDefault && row.fractionInherited;
    const parsedFraction = parseFloat(row.fraction);
    // An inherited fraction is ignored by the backend but the column is non-null, so a blank
    // field on a game-tags-only override falls back to 0 rather than dropping the whole row.
    const fraction = fractionInherited && Number.isNaN(parsedFraction) ? 0 : parsedFraction;
    if (!fractionInherited && Number.isNaN(fraction)) {
      continue;
    }
    attributes[level.toString()] = {
      fraction,
      fractionInherited,
      gameTagsInherited,
      // gameTagsInherited is authoritative: an inheriting level ignores its own tag lists.
      includeGameTags: gameTagsInherited ? undefined : gameTagsToString(row.includeTags),
      excludeGameTags: gameTagsInherited ? undefined : gameTagsToString(row.excludeTags),
    };
  }
  return attributes;
}

function convertAttributesToOverrides(attributes: Record<string, CashbackLevel> | undefined, availableTags: GameTag[]): LevelOverride[] {
  if (!attributes) {
    return [];
  }
  return Object.keys(attributes)
    .sort((left, right) => parseInt(left, 10) - parseInt(right, 10))
    .map((key) => {
      const entry = attributes[key];
      return createLevelOverride(
        key,
        entry.fraction != null ? entry.fraction.toString() : "",
        entry.gameTagsInherited ?? false,
        gameTagsFromString(entry.includeGameTags ?? "", availableTags),
        gameTagsFromString(entry.excludeGameTags ?? "", availableTags),
        entry.fractionInherited ?? false,
      );
    });
}

// A config must always expose level 0, whose fraction and tags are the defaults every level inherits.
function ensureDefaultLevel(overrides: LevelOverride[]): LevelOverride[] {
  if (overrides.some((row) => row.level === DEFAULT_LEVEL)) {
    return overrides;
  }
  return [createLevelOverride(DEFAULT_LEVEL, "0"), ...overrides];
}

// The save-time confirmation gate reuses the inline warning's threshold logic, so every level
// fraction is judged against the config's basis exactly as the inline banner is. The reward type
// selects the rakeback-vs-cashback limit set, matching the per-row advisory.
function collectLargeFractions(
  overrides: LevelOverride[],
  basis: CalculationBasis,
  thresholds: CashbackFractionThresholds | undefined,
  type: CashbackTypeV2 | undefined,
): LargeFractionEntry[] {
  const entries: LargeFractionEntry[] = [];
  for (const row of overrides) {
    // An inheriting level's own fraction is never applied, so its value is not judged against the ceiling.
    if (row.fractionInherited) {
      continue;
    }
    const fraction = Number(row.fraction);
    if (isLargeFraction(fraction, basis, thresholds, type)) {
      entries.push({ label: `${t("cashback.config-v2.level-label", undefined, "Level")} ${row.level}`, fraction });
    }
  }
  return entries;
}

export const EditCashbackConfigV2 = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>();
  const service = getContainer().getCashbackConfigV2Service();
  const rewardService = getContainer().getRewardsConfigService();
  const gameTagService = getContainer().getCasinoGameTagService();
  const thresholds = useCashbackFractionThresholds();

  const [levelOverrides, setLevelOverrides] = useState<LevelOverride[]>([]);
  // Fractions as first loaded, so the blocking ceiling fires only on a value changed this session; a
  // stored over-ceiling fraction is grandfathered. Level overrides key on their stable rowId, so a row
  // added this session has no baseline and any over-ceiling entry in it counts as changed.
  const initialLevelFractions = useRef<Map<string, string>>(new Map());
  const [blockedRowIds, setBlockedRowIds] = useState<string[]>([]);
  const [showFractionDialog, setShowFractionDialog] = useState(false);
  const [fractionEntries, setFractionEntries] = useState<LargeFractionEntry[]>([]);
  const [userCancelledSave, setUserCancelledSave] = useState(false);
  // Resolved when the operator confirms the large-fraction dialog, rejected when they cancel; lets the
  // save await the decision inline instead of splitting the submit into two callbacks.
  const pendingFractionConfirmation = useRef<{ resolve: () => void; reject: (reason: Error) => void } | null>(null);
  const [rewards, setRewards] = useState<RewardTemplate[]>([]);
  const [gameTags, setGameTags] = useState<GameTag[]>([]);

  const [form, formListener, setForm] = useForm<CashbackConfigV2>(
    { status: CashbackConfigStatus.DISABLED, calculationBasis: CalculationBasis.UNWEIGHTED },
    async (): Promise<void> => {
      if (!isValidOverrides(levelOverrides)) {
        throw new Error(
          t("cashback.config-v2.invalid-overrides", undefined, "Level overrides are not valid (check for duplicate levels, negative values, or empty fields)."),
        );
      }

      setUserCancelledSave(false);

      const type = form.model.type;
      const rowIdsBlocked = levelOverrides
        .filter((row) => !row.fractionInherited && isNewlyBlockingFraction(row.fraction, initialLevelFractions.current.get(row.rowId), thresholds, type))
        .map((row) => row.rowId);
      setBlockedRowIds(rowIdsBlocked);
      if (rowIdsBlocked.length > 0) {
        throw new Error(blockedFractionMessage("cashback.config-v2.large-fraction-blocked", blockingFractionLimit(thresholds, type)));
      }

      const basis = form.model.calculationBasis ?? CalculationBasis.UNWEIGHTED;
      const largeFractions = collectLargeFractions(levelOverrides, basis, thresholds, type);
      if (largeFractions.length > 0) {
        setFractionEntries(largeFractions);
        setShowFractionDialog(true);
        try {
          await new Promise<void>((resolve, reject) => {
            pendingFractionConfirmation.current = { resolve, reject };
          });
        } catch (error) {
          // Cancelling is a normal choice, not a failure — suppress the form's error/success banners.
          setUserCancelledSave(true);
          throw error;
        }
      }

      const toSave: CashbackConfigV2 = {
        ...form.model,
        levels: convertOverridesToAttributes(levelOverrides),
      };

      const isNew = match.params.id === "new";
      const savedConfig = await (isNew ? service.save(toSave) : service.save({ ...toSave, id: Number(match.params.id) }));

      setForm({ model: savedConfig, validationMap: form.validationMap });
      const savedOverrides = ensureDefaultLevel(convertAttributesToOverrides(savedConfig.levels, gameTags));
      setLevelOverrides(savedOverrides);
      initialLevelFractions.current = new Map(savedOverrides.map((row) => [row.rowId, row.fraction]));
      if (isNew && savedConfig.id != null) {
        history.replace(`/cashback/v2/config/${savedConfig.id}`);
      }
    },
  );

  const loadReferenceData = (): Promise<[RewardTemplateListResponse, ListGameTagResponse]> =>
    Promise.all([
      rewardService.list({ limit: 1000, offset: 0, orderBy: "name", rewardType: RewardType.cash }),
      gameTagService.list({ limit: 1000, offset: 0, orderBy: "name" }),
    ]);

  const status = useLoadingState(async () => {
    const [rewardsResponse, gameTagsResponse] = await loadReferenceData();
    const allTags = gameTagsResponse.items ?? [];
    setRewards(rewardsResponse.items ?? []);
    setGameTags(allTags);

    if (match.params.id === "new") {
      setLevelOverrides(ensureDefaultLevel([]));
      return;
    }

    const cashbackConfig = await service.getEntity(match.params.id);
    setForm({ model: cashbackConfig, validationMap: {} });
    const loadedOverrides = ensureDefaultLevel(convertAttributesToOverrides(cashbackConfig.levels, allTags));
    setLevelOverrides(loadedOverrides);
    initialLevelFractions.current = new Map(loadedOverrides.map((row) => [row.rowId, row.fraction]));
  }, [match.params.id]);

  const isNewConfig = match.params.id === "new";

  const handleScheduledHourChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const hour = event.target.value;
    setForm({
      model: { ...form.model, scheduledPayoutTime: hour ? formatHour(hour) : undefined },
      validationMap: form.validationMap,
    });
  };

  const renderGeneralSettings = (): JSX.Element => (
    <div className="card-box">
      <div className="ibox-content">
        <h4 className="ibox-title">{t("cashback.config-v2.general-settings-title", undefined, "General Settings")}</h4>
        {!isNewConfig && <div className="hr-line-dashed"></div>}

        {!isNewConfig && (
          <FormGroup className="form-group">
            <label className="col-sm-3 control-label">{t("cashback.config-v2.id-label", undefined, "ID")}</label>
            <div className="col-sm-10">
              <FormInput
                model="id"
                type="integer"
                className="form-control"
                disabled={true}
                data-testid="cashback-v2-id"
              />
            </div>
          </FormGroup>
        )}

        <div className="hr-line-dashed"></div>
        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.config-v2.name-label", undefined, "Name")}</label>
          <div className="col-sm-10">
            <FormInput
              model="name"
              validators={[Validators.required()]}
              type="text"
              className="form-control"
              data-testid="cashback-v2-name"
            />
            <span className="help">{t("cashback.config-v2.name-help", undefined, "Internal name, will not be shown to players")}</span>
          </div>
        </FormGroup>

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.config-v2.status-label", undefined, "Status")}</label>
          <div className="col-sm-10">
            <SelectInput
              model="status"
              values={Object.keys(CashbackConfigStatus)}
              validators={[Validators.required()]}
              className="form-control"
              data-testid="cashback-v2-status-select"
            />
            <span className="help">
              <strong>ENABLED</strong> &mdash; {t("cashback.config-v2.status-help-enabled", undefined, "processed and pays out per the scheduling below.")}{" "}
              <strong>DISABLED</strong> &mdash; {t("cashback.config-v2.status-help-disabled", undefined, "paused, retained for resume.")}{" "}
              <strong>ARCHIVED</strong> &mdash; {t("cashback.config-v2.status-help-archived", undefined, "retired; hidden from default list views.")}
            </span>
          </div>
        </FormGroup>

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.config-v2.type-label", undefined, "Type")}</label>
          <div className="col-sm-10">
            <SelectInput
              model="type"
              values={Object.keys(CashbackTypeV2)}
              validators={[Validators.required()]}
              className="form-control"
              data-testid="cashback-v2-type-select"
            />
            <span className="help">{t("cashback.config-v2.type-help", undefined, "Cashback = based on net losses, Rakeback = based on wagered amount.")}</span>
          </div>
        </FormGroup>

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.config-v2.calculation-basis-label", undefined, "Calculation Basis")}</label>
          <div className="col-sm-10">
            <SelectInput
              model="calculationBasis"
              values={Object.keys(CalculationBasis)}
              validators={[Validators.required()]}
              className="form-control"
              disabled={!isNewConfig}
              data-testid="cashback-v2-calculation-basis-select"
            />
            <span className="help">
              {t(
                "cashback.config-v2.calculation-basis-help",
                undefined,
                "RTP_BASED = weight contributions by each game's house edge. UNWEIGHTED = flat, every wager counts equally.",
              )}
            </span>
          </div>
        </FormGroup>

        {renderFractionInfoCard()}

        <FormGroup className="form-group">
          <label className="col-sm-3 control-label">{t("cashback.config-v2.reward-template-label", undefined, "Cash Reward Template")}</label>
          <div className="col-sm-10">
            <SelectInput
              className="form-control"
              model="cashRewardTemplateId"
              optionModelValue={(reward) => reward.id}
              values={rewards}
              displayValue={(reward) => reward.name}
              data-testid="cashback-v2-reward-template-select"
            />
            <span className="help">
              {t("cashback.config-v2.reward-template-help", undefined, "Cash reward template used for payouts. Currency is overridden per payout.")}
            </span>
          </div>
        </FormGroup>
      </div>
    </div>
  );

  const renderFractionInfoCard = (): JSX.Element => {
    const basis = form.model.calculationBasis ?? CalculationBasis.UNWEIGHTED;
    // The example is anchored on level 0's fraction, the value every other level inherits by default.
    const defaultFractionRow = levelOverrides.find((row) => row.level === DEFAULT_LEVEL);
    const fraction = Number(defaultFractionRow?.fraction);
    const safeFraction = Number.isFinite(fraction) ? fraction : 0;
    const isRtp = basis === CalculationBasis.RTP_BASED;
    // Contribution is the whole wager under UNWEIGHTED, but only the house-edge slice under RTP_BASED.
    const contribution = isRtp ? EXAMPLE_WAGER * EXAMPLE_HOUSE_EDGE : EXAMPLE_WAGER;
    const payout = contribution * safeFraction;

    return (
      <div className="form-group">
        <div className="col-sm-10 col-sm-offset-3">
          <div
            className="alert alert-info"
            data-testid="cashback-v2-fraction-info"
          >
            <strong>{t("cashback.config-v2.fraction-info-title", undefined, "How the fraction applies")}</strong>
            <p className="mt-2 mb-1">
              {t(
                "cashback.config-v2.fraction-info-intro",
                undefined,
                'The fraction multiplies the player\'s contribution for the period. "Contribution" depends on type and basis:',
              )}
            </p>
            <ul className="mb-2">
              <li>
                <strong>RAKEBACK + UNWEIGHTED:</strong> {t("cashback.config-v2.fraction-info-rakeback-unweighted", undefined, "sum of wagers")}
              </li>
              <li>
                <strong>RAKEBACK + RTP_BASED:</strong> {t("cashback.config-v2.fraction-info-rakeback-rtp", undefined, "sum of (wager × game house edge)")}
              </li>
              <li>
                <strong>CASHBACK + UNWEIGHTED:</strong> {t("cashback.config-v2.fraction-info-cashback-unweighted", undefined, "net loss (wagers − wins)")}
              </li>
              <li>
                <strong>CASHBACK + RTP_BASED:</strong> {t("cashback.config-v2.fraction-info-cashback-rtp", undefined, "net loss weighted by game house edge")}
              </li>
            </ul>
            <p className="mb-1">
              {t(
                "cashback.config-v2.fraction-info-example-intro",
                { wager: EXAMPLE_WAGER, rtpPercent: EXAMPLE_RTP_PERCENT },
                "For €{{wager}} wagered and lost on a {{rtpPercent}}% RTP slot, with the level 0 (default) fraction:",
              )}
            </p>
            <ul
              className="mb-2"
              data-testid="cashback-v2-fraction-example"
            >
              {isRtp ? (
                <li>
                  <strong>RTP_BASED:</strong> (&euro;{EXAMPLE_WAGER} &times; {EXAMPLE_HOUSE_EDGE}) &times; {safeFraction} ={" "}
                  <strong>&euro;{formatEuro(payout)}</strong>
                </li>
              ) : (
                <li>
                  <strong>UNWEIGHTED:</strong> &euro;{EXAMPLE_WAGER} &times; {safeFraction} = <strong>&euro;{formatEuro(payout)}</strong>
                </li>
              )}
            </ul>
            <p className="mb-0">
              {t(
                "cashback.config-v2.fraction-info-warning",
                undefined,
                "UNWEIGHTED needs a much smaller fraction than RTP_BASED. Otherwise it pays out roughly 20–30× more than intended on typical slots.",
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderSchedulingSection = (): JSX.Element => {
    const frequency = form.model.frequency;
    const selectedHour = parseHour(form.model.scheduledPayoutTime);

    return (
      <div className="card-box">
        <div className="ibox-content">
          <h4 className="ibox-title">{t("cashback.config-v2.scheduling-title", undefined, "Scheduling")}</h4>

          <FormGroup className="form-group">
            <label className="col-sm-3 control-label">{t("cashback.config-v2.recurrence-label", undefined, "Recurrence")}</label>
            <div className="col-sm-10">
              <SelectInput
                model="frequency"
                values={Object.keys(CashbackFrequencyV2)}
                validators={[Validators.required()]}
                className="form-control"
                data-testid="cashback-v2-frequency-select"
              />
              <span className="help">{t("cashback.config-v2.recurrence-help", undefined, "How often the cashback should perform a payout.")}</span>
            </div>
          </FormGroup>

          {frequency && frequency !== CashbackFrequencyV2.INSTANT && (
            <FormGroup className="form-group">
              <label className="col-sm-3 control-label">{t("cashback.config-v2.payout-hour-label", undefined, "Payout Hour (UTC)")}</label>
              <div className="col-sm-10">
                <select
                  className="form-control"
                  value={selectedHour}
                  onChange={handleScheduledHourChange}
                  data-testid="cashback-v2-payout-hour-select"
                >
                  <option value="">--</option>
                  {HOURS.map((hour) => (
                    <option
                      key={hour}
                      value={hour}
                    >
                      {hour}:00
                    </option>
                  ))}
                </select>
                <span className="help">{t("cashback.config-v2.payout-hour-help", undefined, "Cashback runs once per hour, on the hour. Time is in UTC.")}</span>
              </div>
            </FormGroup>
          )}

          {frequency === CashbackFrequencyV2.WEEKLY && (
            <FormGroup className="form-group">
              <label className="col-sm-3 control-label">{t("cashback.config-v2.payout-day-of-week-label", undefined, "Payout Day of Week")}</label>
              <div className="col-sm-10">
                <SelectInput
                  model="payoutDayOfWeek"
                  values={Object.keys(DayOfWeek)}
                  className="form-control"
                  data-testid="cashback-v2-payout-day-of-week-select"
                />
                <span className="help">{t("cashback.config-v2.payout-day-of-week-help", undefined, "Only applies for Weekly payout.")}</span>
              </div>
            </FormGroup>
          )}

          {frequency === CashbackFrequencyV2.MONTHLY && (
            <FormGroup className="form-group">
              <label className="col-sm-3 control-label">{t("cashback.config-v2.payout-day-of-month-label", undefined, "Payout Day of Month")}</label>
              <div className="col-sm-10">
                <FormInput
                  model="payoutDayOfMonth"
                  validators={[Validators.number(), Validators.range(1, 28)]}
                  type="integer"
                  className="form-control"
                  data-testid="cashback-v2-payout-day-of-month"
                />
                <span className="help">
                  {t("cashback.config-v2.payout-day-of-month-help", undefined, "Only applies for Monthly payout. Must be between 1 and 28.")}
                </span>
              </div>
            </FormGroup>
          )}
        </div>
      </div>
    );
  };

  // A cancelled confirmation or a save blocked on an over-ceiling fraction is a deliberate outcome,
  // not a form error, so the generic submit-error banner stays hidden in those cases.
  const showSubmitError = !userCancelledSave && blockedRowIds.length === 0;

  return (
    <RouterViewTemplate
      status={status}
      title={t("cashback.config-v2.title", undefined, "Edit Cashback (V2)")}
    >
      <div className="wrapper wrapper-content animated fadeInRight">
        <Form
          className="form form-horizontal"
          validationMap={form.validationMap}
          modelObject={form.model}
          formListener={formListener}
        >
          <div className="row">
            <div className="col-lg-12">
              {renderGeneralSettings()}
              {renderSchedulingSection()}
              <V2UserLevelsEditor
                overrides={levelOverrides}
                onChange={setLevelOverrides}
                basis={form.model.calculationBasis ?? CalculationBasis.UNWEIGHTED}
                thresholds={thresholds}
                availableTags={gameTags}
                rewardType={form.model.type}
                blockedRowIds={blockedRowIds}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="card-box">
                {showSubmitError && <FormSubmitError />}
                {!userCancelledSave && <FormSubmitSuccess text={t("cashback.config-v2.save-success", undefined, "Cashback was updated successfully")} />}
                <div className="form-group">
                  <div className="col-sm-12">
                    <Submit
                      className="btn btn-primary"
                      data-testid="cashback-v2-save"
                    >
                      {t("cashback.config-v2.save", undefined, "Save")}
                    </Submit>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <LargeFractionConfirmationDialog
            show={showFractionDialog}
            entries={fractionEntries}
            threshold={thresholds ? largeFractionThreshold(form.model.calculationBasis ?? CalculationBasis.UNWEIGHTED, thresholds, form.model.type) : 0}
            onConfirm={() => {
              setShowFractionDialog(false);
              pendingFractionConfirmation.current?.resolve();
              pendingFractionConfirmation.current = null;
            }}
            onCancel={() => {
              setShowFractionDialog(false);
              pendingFractionConfirmation.current?.reject(new Error("USER_CANCELLED"));
              pendingFractionConfirmation.current = null;
            }}
          />
        </Form>
      </div>
    </RouterViewTemplate>
  );
};

export default EditCashbackConfigV2;
